import { useState, useCallback } from 'react';
import { Checkbox, Input, Select, Space, Switch, Typography } from 'antd';
import { EditFilled, MailFilled, PhoneFilled } from '@ant-design/icons';
import PropTypes from 'prop-types';

export const InlineEditableInput = ({
    value,
    record,
    onChange,
    fieldName,
    children,
    canEdit = false,
    type = "text",
    options = [],
    optionFilterProp = "children",
    placeholder, loading = false,
    clickToEdit = false,
}) => {
    if (!onChange || !fieldName) {
        throw new Error('InlineEditableInput: All props (value, onChange, fieldName) are required.');
    }

    const [inputValue, setInputValue] = useState(value);
    const [isEditing, setIsEditing] = useState(false);

    const toggleEdit = useCallback(() => setIsEditing(prev => !prev), []);

    const handleChange = useCallback(e => {
        const newValue = e?.target ? e.target.value : e;
        setInputValue(newValue);
    }, []);

    const save = useCallback(() => {
        onChange(inputValue, fieldName, record);
        setIsEditing(false);
    }, [inputValue, onChange, fieldName, record]);

    // Configuration des propriétés communes
    const inputProps = {
        size: "small",
        value: inputValue,
        onChange: handleChange,
        onBlur: save,
        onPressEnter: save,
        autoFocus: true
    };

    const renderInput = () => {
        switch (type) {
            case 'number':
                return <Input style={{ width: '100%' }} {...inputProps} type="number" />;
            case 'date':
                return <Input style={{ width: '100%' }} {...inputProps} type="date" />;
            case 'datetime-local':
                return <Input style={{ width: '100%' }} {...inputProps} type="datetime-local" />;
            case 'email':
                return <Input prefix={<MailFilled />} style={{ width: '100%' }} {...inputProps} type="email" />;
            case 'password':
                return <Input style={{ width: '100%' }} {...inputProps} type="password" />;
            case 'textarea':
                return <Input.TextArea style={{width: "100%"}} {...inputProps} />;
            case 'checkbox':
                return <Checkbox checked={inputValue} onChange={e => handleChange(e.target.checked)} onBlur={save} autoFocus />;
            case 'switch':
                return <Switch checked={inputValue} onChange={handleChange} onBlur={save} autoFocus />;
            case 'tel':
                return <Input prefix={<PhoneFilled />} style={{ width: '100%' }} {...inputProps} type="tel" />
            case 'select':
                return (
                    <Select loading={loading} placeholder={placeholder ?? "Sélectionner une option"}
                        size="large"
                        style={{ width: '100%' }}
                        value={inputValue}
                        onChange={handleChange}
                        onBlur={save}
                        autoFocus
                        showSearch
                        optionFilterProp={optionFilterProp}
                        options={options}
                    />
                );
            default:
                return <Input {...inputProps} />;
        }
    };

    // La largeur minimale n'a de sens qu'en édition (pour laisser de la place
    // au Select/Input) : l'imposer aussi en lecture casse les layouts compacts
    // (ex. ActivityItem) qui n'ont pas 200px à offrir sur mobile.
    return (
        <div style={isEditing ? { minWidth: "200px" } : undefined}>
            {isEditing ? renderInput() : clickToEdit ? (
                <Typography.Text
                    onClick={canEdit ? toggleEdit : undefined}
                    style={canEdit ? { cursor: 'pointer' } : undefined}
                >
                    {children}
                </Typography.Text>
            ) : (
                <Space>
                    <Typography.Text>{children}</Typography.Text>
                    {!!canEdit && (<EditFilled onClick={toggleEdit} />)}
                </Space>
            )}
        </div>
    );
};

InlineEditableInput.propTypes = {
    value: PropTypes.any,
    record: PropTypes.object,
    onChange: PropTypes.func,
    fieldName: PropTypes.string,
    children: PropTypes.node,
    canEdit: PropTypes.bool,
    type: PropTypes.string,
    options: PropTypes.array,
    optionFilterProp: PropTypes.string,
    placeholder: PropTypes.string,
    loading: PropTypes.bool,
    clickToEdit: PropTypes.bool,
};